// Onboarding diagnostics — the single client-side definition of the intro
// onboarding steps, the diagnostic event names and the onboarding version.
// Web and mobile import this; the backend keeps a CommonJS mirror in
// apps/backend/src/config/onboardingDiagnostics.js (a backend test checks the
// two stay in sync).
//
// The steps are the ones the apps actually show, in order:
//   dob      — "When's your birthday?" (only accounts created without a DOB:
//              Google / Apple sign-in). Shown before onboarding_started.
//   welcome  — "Let's build your collection" → Get started
//   titles   — "Pick a few movies you've watched" — optional since version 2:
//              Continue with any number of picks, or Skip for now with none
//              (version 1 required ≥ 3)
//   genres   — "What do you like watching?" → Finish / Skip for now; this is
//              where POST /onboarding saves titles + genres and onboarding
//              completes server-side
//   verify   — "You're all set!" → Earn coin (first quiz) / Explore. Shown after
//              onboarding is complete; it is the quiz handoff, not a gate. Since
//              version 2 it is bypassed automatically when no title was picked.

// Bump when the onboarding flow itself changes (steps, copy, requirements) so
// the admin dashboard can compare cohorts before and after the change.
//   1 — title selection required ≥ 3 picks
//   2 — title selection optional; verify auto-skipped without picks
export const ONBOARDING_VERSION = 2;

// Saved-progress drafts from these versions resume safely: the steps did not
// change between them (version 2 only dropped the title minimum).
export const ONBOARDING_DRAFT_VERSIONS = Object.freeze([1, 2]);

export const ONBOARDING_STEPS = Object.freeze({
    DOB: Object.freeze({ key: 'dob', number: 0 }),
    WELCOME: Object.freeze({ key: 'welcome', number: 1 }),
    TITLES: Object.freeze({ key: 'titles', number: 2 }),
    GENRES: Object.freeze({ key: 'genres', number: 3 }),
    VERIFY: Object.freeze({ key: 'verify', number: 4 }),
});

// Server-logged (activation_events) and mirrored to Firebase / GA4.
// onboarding_step_completed already existed (welcome / titles); the rest are new.
export const ONBOARDING_DIAGNOSTIC_EVENTS = Object.freeze({
    STEP_VIEWED: 'onboarding_step_viewed',
    STEP_ACTION: 'onboarding_step_action',       // user tapped the step's CTA
    STEP_COMPLETED: 'onboarding_step_completed', // the step's action succeeded
    STEP_ERROR: 'onboarding_step_error',         // a technical failure on the step
    EXITED: 'onboarding_exited',                 // left the flow (Android back on Welcome)
    APP_BACKGROUNDED: 'onboarding_app_backgrounded',
    RESUMED: 'onboarding_resumed',               // re-entered with saved progress / after an unclean exit
    STEP_AUTO_SKIPPED: 'onboarding_step_auto_skipped', // the app bypassed a step that had nothing to show (`reason`)
    QUIZ_SUBMIT_FAILED: 'quiz_submit_failed',
    QUIZ_ABANDONED: 'quiz_abandoned',
});

// Values for the `action` property of onboarding_step_action.
export const ONBOARDING_ACTIONS = Object.freeze({
    CONTINUE: 'continue',                  // the primary CTA (Get started / Continue / Finish / Earn coin)
    SKIP: 'skip',                          // "Skip for now" on titles (nothing picked) / genres
    EXPLORE: 'explore',                    // "Explore more titles" on verify
    CONTINUE_DISABLED: 'continue_disabled', // tapped the CTA while it was still disabled (not an attempt)
    BACK: 'back',                          // went back one step inside the flow
});

// `reason` on a skip. A user's own skip is never an abandonment.
export const ONBOARDING_SKIP_REASONS = Object.freeze({
    USER_SKIPPED: 'user_skipped',             // tapped "Skip for now"
    NO_SELECTED_MOVIES: 'no_selected_movies', // verify auto-skipped: no title was picked
    NO_ELIGIBLE_MOVIES: 'no_eligible_movies', // verify auto-skipped: none of the picks can start a challenge
});

export const ONBOARDING_ERROR_TYPES = Object.freeze({
    NETWORK: 'network',           // no response (offline, DNS, reset)
    TIMEOUT: 'timeout',           // no response before the client timeout
    SERVER: 'server',             // HTTP 5xx
    AUTH: 'auth',                 // HTTP 401 / session expired
    VALIDATION: 'validation',     // HTTP 400 / 422
    CLIENT: 'client',             // any other HTTP 4xx
    BAD_RESPONSE: 'bad_response', // non-JSON / unexpected response shape
    RENDER_CRASH: 'render_crash', // a JS render error caught by the error boundary
    UNKNOWN: 'unknown',
});

const ERROR_CODE_PATTERN = /^[a-z0-9_.-]{1,48}$/i;

/**
 * Classify a failed API call (the ApiError both apps throw, or anything else)
 * into the non-sensitive fields recorded on onboarding_step_error. Never reads
 * request bodies, headers or tokens — only the status code, the backend's
 * machine-readable error code and the error class.
 */
export function describeOnboardingError(error) {
    const status = Number.isInteger(error?.statusCode) ? error.statusCode : null;
    const rawCode = error?.code ?? null;
    const errorCode = typeof rawCode === 'string' && ERROR_CODE_PATTERN.test(rawCode) ? rawCode : null;

    let errorType = ONBOARDING_ERROR_TYPES.UNKNOWN;
    if (status === 0 || errorCode === 'network_error') {
        errorType = error?.payload?.timedOut ? ONBOARDING_ERROR_TYPES.TIMEOUT : ONBOARDING_ERROR_TYPES.NETWORK;
    } else if (status && status >= 500) {
        errorType = ONBOARDING_ERROR_TYPES.SERVER;
    } else if (status === 401) {
        errorType = ONBOARDING_ERROR_TYPES.AUTH;
    } else if (status === 400 || status === 422) {
        errorType = ONBOARDING_ERROR_TYPES.VALIDATION;
    } else if (status && status >= 400) {
        errorType = ONBOARDING_ERROR_TYPES.CLIENT;
    } else if (error instanceof SyntaxError || /unexpected response/i.test(String(error?.message || ''))) {
        errorType = ONBOARDING_ERROR_TYPES.BAD_RESPONSE;
    }
    // A non-JSON body (HTML error page) arrives as an ApiError with a status but no code.
    if (status && /unexpected response/i.test(String(error?.message || ''))) {
        errorType = ONBOARDING_ERROR_TYPES.BAD_RESPONSE;
    }

    return {
        error_type: errorType,
        ...(status !== null && { http_status: status }),
        ...(errorCode && errorCode !== 'network_error' && { error_code: errorCode }),
    };
}
